import React, { useMemo, useRef, useState } from "react";
import {
  DataTable,
  DataTablePageEvent,
  DataTableProps,
  DataTableRowReorderEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Column, ColumnBodyOptions, ColumnProps } from "primereact/column";
import {
  configProps,
  DataGridDropdowns,
  RowFromView,
  ViewNames,
} from "../types";
import StatusButtons from "./StatusButtons";
import { RowCancelButtonBodyFunction } from "./RowCancelButton";
import { useErrorMarking } from "../contexts/ErrorMarkingContext";
import { useEditMode } from "../contexts/EditModeContext";
import { createRenderCellEditor } from "./RenderCellEditor";
import { createOnCellEditComplete } from "../helpers/onCellEditComplete";
import { Button } from "primereact/button";
import { HistoryDialog } from "./HistoryDialog";
import { CommentsDialog } from "./CommentsDialog";
import { useSetDataByView } from "../hooks/useSetDataByView/useSetDataByView";
import { useAllLoadingCells } from "../redux/slices/loadingCells.ts";
import { useView } from "../contexts/ViewContext.tsx";
import { localisedValue } from "./LocalisedValue.tsx";
import { useSignal } from "@preact/signals-react";
import { useShowToast } from "../contexts/ToastContext.tsx";
import AddNewEntry from "./AddNewEntry.tsx";
import { useAppDispatch } from "../redux/store.ts";
import { createFormSlice } from "../redux/slices/createForm.ts";

type DataGridProps<
  TView extends ViewNames,
  TDropdowns extends DataGridDropdowns,
> = {
  data: RowFromView<TView>[];
  visibleColumns: configProps<TView>["columns"];
  config: configProps<TView>;
  dropdownConfigs: TDropdowns;
  globalFilterValue: string;
  isLoading?: boolean;
  isFetching?: boolean;
  page: number;
  rowsPerPage: number;
  onPage: (e: DataTablePageEvent) => void;
  totalRecords?: number;
  sortField: string;
  sortOrder: number;
  onSort: (e: DataTableSortEvent) => void;
  noStatusButtons?: boolean;
  reorderableRows?: boolean;
  onRowReorder?: (e: DataTableRowReorderEvent<RowFromView<TView>[]>) => void;
  showRowReorder?: boolean;
};

const rowsPerPageOptions = [10, 25, 50, 100];

export default function DataGrid<
  TDropdowns extends DataGridDropdowns,
  TView extends Exclude<
    ViewNames,
    "drivers" | "locations" | "companies" | "products"
  >,
>({
  data,
  visibleColumns,
  config,
  dropdownConfigs,
  globalFilterValue,
  isLoading,
  isFetching,
  page,
  rowsPerPage,
  onPage,
  totalRecords,
  sortField,
  sortOrder,
  onSort,
  noStatusButtons = false,
  reorderableRows = false,
  onRowReorder,
  showRowReorder = false,
}: DataGridProps<TView, TDropdowns>) {
  const ColumnsByField = useMemo(() => {
    return config.columns.reduce(
      (acc, column) => {
        acc[column.field] = column;
        return acc;
      },
      {} as Record<string, configProps<TView>["columns"][number]>,
    );
  }, [config.columns]);

  const dataSignal = useSignal<RowFromView<TView>[]>(data);

  React.useEffect(() => {
    const freshData = data;
    const persistData = dataSignal.value;

    if (freshData.length !== persistData.length) {
      dataSignal.value = freshData;
    } else {
      freshData.forEach((row, index) => {
        if (JSON.stringify(row) !== JSON.stringify(persistData[index])) {
          dataSignal.value[index] = row;
        }
      });
    }
  }, [data, dataSignal]);

  const errorMarking = useErrorMarking();
  const errorMarkingSignal = useSignal(errorMarking);
  React.useEffect(() => {
    // Update the signal when isEditMode changes
    errorMarkingSignal.value = errorMarking;
  }, [errorMarking, errorMarkingSignal]);

  const [selectedHistoryRow, setSelectedHistoryRow] =
    useState<RowFromView<TView> | null>(null);

  const [selectedCommentsRow, setSelectedCommentsRow] =
    useState<RowFromView<TView> | null>(null);

  const { isEditMode } = useEditMode();
  const dispatch = useAppDispatch();

  // We use a signal to wrap the isEditMode state, so that it doesn't case unnecessary re-renders
  const isEditModeSignal = useSignal(isEditMode);

  React.useEffect(() => {
    // Update the signal when isEditMode changes
    isEditModeSignal.value = isEditMode;
  }, [isEditMode, isEditModeSignal]);

  const dataTableRef = useRef<DataTable<RowFromView<TView>[]>>(null);

  const { setData } = useSetDataByView<TView>();
  const showToast = useShowToast();

  const renderCellEditor = useMemo(
    () =>
      createRenderCellEditor(config.columns, dropdownConfigs, () =>
        dataTableRef.current?.closeEditingCell(),
      ),
    [config.columns, dropdownConfigs],
  );

  const onCellEditComplete = React.useMemo(() => {
    return createOnCellEditComplete<RowFromView<TView>, TView>({
      setData,
      showToast,
    });
  }, [setData, showToast]);
  const loadingCells = useAllLoadingCells();
  const loadingCellsSignal = useSignal(loadingCells);

  React.useEffect(() => {
    // Update the signal when loadingCells changes
    loadingCellsSignal.value = loadingCells;
  }, [loadingCells, loadingCellsSignal]);

  const view = useView();

  const handleCellClick = React.useCallback(
    ({
      rowData: rowToMark,
      field,
    }: {
      rowData: RowFromView<TView>;
      field: string;
    }) => {
      const { isErrorMarkingMode, errorMarkingScope } =
        errorMarkingSignal.value;
      if (!isErrorMarkingMode || field === "entryStatus") return;

      const prevMarked = rowToMark.marked;
      let newMarked: string[] = [];
      switch (errorMarkingScope) {
        case "row": {
          const allFields = config.columns.map((column) => column.field);
          const isFullyMarked = allFields.every((field) =>
            prevMarked.includes(field),
          );
          newMarked = isFullyMarked
            ? prevMarked.filter((field) => !allFields.includes(field))
            : [...new Set([...prevMarked, ...allFields])];
          break;
        }

        case "cell": {
          const isMarked = prevMarked.includes(field);
          newMarked = isMarked
            ? prevMarked.filter((item) => item !== field)
            : [...new Set([...prevMarked, field])];
          break;
        }

        default:
          break;
      }

      dataSignal.value
        .filter((row) => row.id === rowToMark.id)
        .forEach((row) => {
          row.marked = newMarked;
        });
      setData([{ id: rowToMark.id, marked: newMarked }])
        .then((result) => {
          // Optimistically update the row data based on the mutation result
          if (result && result.length > 0 && result[0]?.data) {
            const rowIndex = dataSignal.value.findIndex(
              (row) => row.id === rowToMark.id,
            );

            const markedFieldsFromApi =
              "marked_fields" in result[0].data
                ? result[0].data.marked_fields
                : [];
            if (rowIndex !== -1) {
              dataSignal.value[rowIndex] = {
                ...dataSignal.value[rowIndex],
                marked: markedFieldsFromApi,
              };
            }
          } else if (result && result.length > 0 && result[0]?.error) {
            dataSignal.value
              .filter((row) => row.id === rowToMark.id)
              .forEach((row) => {
                row.marked = prevMarked;
              });
          }
        })
        .catch((error) => {
          dataSignal.value
            .filter((row) => row.id === rowToMark.id)
            .forEach((row) => {
              row.marked = prevMarked;
            });
          console.log(error);
        });
    },
    [errorMarkingSignal, setData, config.columns, dataSignal],
  );

  const handleOonBeforeCellEditShow = React.useCallback<
    NonNullable<ColumnProps["onBeforeCellEditShow"]>
  >(
    ({ rowData, field }) => {
      // NOTE: we need to call the marking logic here, because the DataTable doesn't fire this event when editing is enabled
      if (errorMarkingSignal.value.isErrorMarkingMode) {
        handleCellClick({ rowData: rowData as RowFromView<TView>, field });
        return false; // app should prevent editing mode from being enabled same time as marking mode but this is a safeguard
      }
      if (
        ColumnsByField[field].inputType === "toggle" &&
        isEditModeSignal.value
      ) {
        const update = async () => {
          await setData([
            {
              id: (rowData as RowFromView<TView>).id,
              [field]: !(rowData as RowFromView<TView>)[
                field as keyof RowFromView<TView>
              ],
            },
          ]);
        };
        void update();
        return false;
      }
      return isEditModeSignal.value;
    },
    [
      ColumnsByField,
      setData,
      errorMarkingSignal,
      handleCellClick,
      isEditModeSignal,
    ],
  );

  const rowClassName = React.useCallback<
    NonNullable<DataTableProps<RowFromView<TView>[]>["rowClassName"]>
  >((rowData): string => {
    switch (rowData.entryStatus) {
      case "completed":
        return "completed";
      case "pending":
        return "pending";
      case "cancelled":
        return "cancelled-row";
      case "deleted":
        return "cancelled-row";
      default:
        return "";
    }
  }, []);

  const columnHeader = React.useCallback(
    ({
      editable,
      header,
      headerIcon,
      headerIconTitle,
    }: configProps<TView>["columns"][number]) => {
      if (editable === false && headerIcon) {
        return <span>{header}</span>;
      }
      return (
        <div className="column-header--icon">
          {/* TODO: Maybe this want to be a tooltip rather than a tile attr */}
          <i className={`pi pi-${headerIcon}`} title={headerIconTitle} />
          <span>{header}</span>
        </div>
      );
    },
    [],
  );

  const handleBodyClassName = React.useCallback(
    (rowData: RowFromView<TView>, options: ColumnBodyOptions) => {
      const isCellMarked = (row: RowFromView<TView>, field: string) => {
        if (!row?.marked) return false;

        return row.marked?.includes(field) || false;
      };

      const isCellLoading = loadingCellsSignal.value.find(
        (cell) => cell === `${view}.${rowData.id}.${options.field}`,
      );
      const isMarked = isCellMarked(rowData, options.field);
      const isEditable = ColumnsByField[options.field].editable !== false;
      return [
        isMarked ? "error-marked-cell" : "",
        isEditable ? "editable-cell" : "non-editable-cell",
        isCellLoading ? "loading-cell" : "",
      ]
        .filter(Boolean)
        .join(" ");
    },
    [ColumnsByField, loadingCellsSignal, view],
  );

  const globalFilterFields = useMemo(() => {
    return visibleColumns.map((column) => column.field);
  }, [visibleColumns]);

  const showCancelColumn = React.useMemo(() => {
    return config.columns.some((row) => row.field === "deliveryStatus");
  }, [config.columns]);

  const onHideHistory = React.useCallback(() => {
    setSelectedHistoryRow(null);
  }, []);

  const onHideComments = React.useCallback(() => {
    setSelectedCommentsRow(null);
  }, []);

  const stickyColumnBody = React.useCallback(
    (rowData: RowFromView<TView>) => (
      <div className="frozen-buttons-container">
        {/* Button to create Transport Order from ontract */}
        {(config.view === "contracts" || config.view === "goodsToTakeAway") && (
          <Button
            icon="pi pi-truck"
            text
            size="small"
            onClick={() => {
              dispatch(
                createFormSlice.actions.openForm({
                  mode: "create",
                  dialogView: "transportOrders",
                  initialValues: {
                    [config.view === "contracts"
                      ? "salesContract"
                      : "purchaseContract"]: rowData.id,
                    repeatCount: 1,
                  },
                }),
              );
            }}
          />
        )}
        {/* History button */}
        <Button
          size="small"
          icon="pi pi-history"
          text
          onClick={(e) => {
            e.stopPropagation();
            setSelectedHistoryRow(rowData);
          }}
        />
        {/* Comments button with badge */}
        <Button
          size="small"
          className="wide"
          icon="pi pi-comment"
          text={rowData.commentsCount === 0}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedCommentsRow(rowData);
          }}
        />
        {/* Status buttons */}
        {!noStatusButtons && (
          <StatusButtons<TView>
            rowData={rowData}
            fieldNameMap={config.fieldNameMap}
            columns={config.columns}
            viewName={config.view}
          />
        )}
        {showCancelColumn && RowCancelButtonBodyFunction(rowData)}
      </div>
    ),
    [
      config.columns,
      config.fieldNameMap,
      config.view,
      dispatch,
      showCancelColumn,
      noStatusButtons,
    ],
  );

  return (
    <div>
      <DataTable<RowFromView<TView>[]>
        ref={dataTableRef}
        value={dataSignal.value}
        dataKey="id"
        size="small"
        lazy
        paginator
        rows={rowsPerPage}
        first={page * rowsPerPage}
        totalRecords={totalRecords}
        onPage={onPage}
        rowsPerPageOptions={rowsPerPageOptions}
        rowClassName={rowClassName}
        scrollable
        scrollHeight="75vh"
        sortField={sortField}
        sortOrder={(sortOrder ?? 1) as 1 | -1}
        onSort={onSort}
        cellSelection
        selectionMode="single"
        // Note: we don't toggle this value, as when it's toggled, the DataTable re-renders all cells
        editMode={"cell"}
        // Note: This function doesn't get called when a cell has an editor/editing enabled so we call
        onCellClick={handleCellClick}
        globalFilter={globalFilterValue}
        globalFilterFields={globalFilterFields}
        emptyMessage={
          isLoading ? "Ładowanie danych..." : "Nie znaleziono danych!"
        }
        loading={isFetching}
        cellMemo={true}
        cellMemoPropsDepth={3}
        reorderableRows={reorderableRows}
        onRowReorder={onRowReorder}
      >
        {showRowReorder ? (
          <Column
            field="sort_rank"
            rowReorder
            reorderable={onRowReorder !== undefined}
            className="reorder-column"
            frozen={true}
            sortable={false}
            body=""
          />
        ) : null}
        {visibleColumns.map((entry) => (
          <Column
            key={entry.id}
            field={entry.field}
            body={entry.body || localisedValue}
            header={columnHeader(entry)}
            className={entry.className}
            sortable={entry.sortable ?? true}
            editor={entry.editable !== false && renderCellEditor}
            onCellEditComplete={onCellEditComplete}
            cellEditValidateOnClose
            bodyClassName={handleBodyClassName}
            onBeforeCellEditShow={handleOonBeforeCellEditShow}
          ></Column>
        ))}
        <Column
          field="entryStatus"
          className="status-column"
          frozen={true}
          sortable={true}
          alignFrozen="right"
          body={stickyColumnBody}
        />
      </DataTable>
      <AddNewEntry<TView, TDropdowns>
        columns={config.columns}
        dropdownConfigs={dropdownConfigs}
        config={config}
        inputContext="deliveryDialog"
      />
      <HistoryDialog<TView>
        rowData={selectedHistoryRow}
        onHide={onHideHistory}
        columns={config.columns}
      />
      <CommentsDialog<TView>
        rowData={selectedCommentsRow}
        onHide={onHideComments}
        columns={config.columns}
      />
    </div>
  );
}
